import { computeRandevuForSablonLine } from './islemSablonDates';

export function startOfDay(val) {
    const d = new Date(val);
    if (isNaN(d)) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function skipSunday(d, direction = 1) {
    const nd = new Date(d);
    if (nd.getDay() === 0) nd.setDate(nd.getDate() + (direction < 0 ? -1 : 1));
    return nd;
}

export function calcDeltaDays(laterVal, earlierVal) {
    const later = startOfDay(laterVal);
    const earlier = startOfDay(earlierVal);
    if (!later || !earlier) return 0;
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    return Math.floor((later.getTime() - earlier.getTime()) / MS_PER_DAY);
}

/**
 * Kaynak satırın randevu tarihinden sonraki satırların CI_RAN_TAR alanını deltaDays kadar öteler.
 */
export function computeRandevuOteleme(islemList, sourceRow, deltaDays, options = {}) {
    const {
        skipCompleted = true,
        casRef = null,
        baseRanTar = null,
    } = options;

    if (!deltaDays || !sourceRow) {
        return { updates: [], shiftedCount: 0, deltaDays: 0 };
    }

    const baseDate = startOfDay(baseRanTar ?? sourceRow?.CI_RAN_TAR);
    if (!baseDate) return { updates: [], shiftedCount: 0, deltaDays: 0 };

    const baseTime = baseDate.getTime();
    const sourceRef = Number(sourceRow?.CI_REF ?? sourceRow?.ci_ref ?? 0);
    const sourceCas = Number(casRef ?? sourceRow?.CI_CAS_REF ?? 0);
    const updates = [];

    (islemList || []).forEach((rec) => {
        if (!rec?.CI_RAN_TAR) return;
        const recRef = Number(rec?.CI_REF ?? rec?.ci_ref ?? 0);
        if (recRef > 0 && recRef === sourceRef) return;
        if (sourceCas > 0 && Number(rec?.CI_CAS_REF ?? 0) !== sourceCas) return;
        const rStart = startOfDay(rec.CI_RAN_TAR);
        if (!rStart) return;
        const cmp = rStart.getTime() - baseTime;
        // Aynı gündeki daha önceki satır kalır; aynı gün sonraki satır ve sonraki günler kayar.
        if (cmp < 0) return;
        if (cmp === 0 && recRef <= sourceRef) return;
        if (skipCompleted && rec.CI_ISL_TAR) return;
        const shifted = new Date(rStart);
        shifted.setDate(shifted.getDate() + deltaDays);
        updates.push({
            CI_REF: Number(rec?.CI_REF ?? rec?.ci_ref ?? 0),
            CI_RAN_TAR: skipSunday(shifted, deltaDays),
        });
    });

    return { updates: updates.filter((u) => u.CI_REF > 0), shiftedCount: updates.filter((u) => u.CI_REF > 0).length, deltaDays };
}

export function mergeIslemUpdates(baseList, updates) {
    if (!Array.isArray(updates) || updates.length === 0) return baseList;
    const map = new Map(updates.map((u) => [Number(u.CI_REF), u]));
    return (baseList || []).map((row) => {
        const patch = map.get(Number(row?.CI_REF ?? 0));
        return patch ? { ...row, ...patch } : row;
    });
}

export function formatRandevuShiftMessage(count, deltaDays) {
    const gun = Math.abs(Number(deltaDays) || 0);
    const yon = Number(deltaDays) < 0 ? 'öne çekildi' : 'ötelendi';
    return `${count} randevu ${gun} gün ${yon} (Pazar günleri atlandı).`;
}

export function confirmRandevuOteleme(deltaDays) {
    if (!deltaDays) return true;
    const gun = Math.abs(deltaDays);
    const yon = deltaDays < 0 ? 'öne çekilecek' : 'ötelenecek';
    return window.confirm(
        `Randevu tarihinden ${gun} gün fark var.\n\n` +
        `Sonraki randevular ${gun} gün ${yon}. Devam edilsin mi?`
    );
}

function rowRef(row) {
    return Number(row?.CI_REF ?? row?.ci_ref ?? 0);
}

function isCompletedRow(row) {
    return row?.CI_ISL_TAR != null && row.CI_ISL_TAR !== '';
}

function isPassiveSablonLine(row) {
    const value = row?.IS_SILINDI ?? row?.is_silindi;
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 't';
}

function sablonAdOf(row) {
    let raw = row?.CI_DETAY;
    if (raw == null || raw === '') return '';
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            return '';
        }
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return '';
    return String(raw.sablonAd ?? raw.sablon_ad ?? '').trim();
}

function compareByRanTar(a, b) {
    const ta = startOfDay(a?.CI_RAN_TAR)?.getTime() ?? Number.POSITIVE_INFINITY;
    const tb = startOfDay(b?.CI_RAN_TAR)?.getTime() ?? Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return rowRef(a) - rowRef(b);
}

function addDays(base, days) {
    const next = startOfDay(base);
    if (!next) return null;
    next.setDate(next.getDate() + days);
    return next;
}

function offsetParts(line) {
    const yil = Number(line?.IS_YILSAY) || 0;
    const ay = Number(line?.IS_AYSAY) || 0;
    const gun = Number(line?.IS_GUNSAY) || 0;
    const extra = !line?.IS_ILKGUN_DAHIL && (yil > 0 || ay > 0 || gun > 0) ? 1 : 0;
    return { yil, ay, gun, extra };
}

function applyOffset(base, parts) {
    const next = startOfDay(base);
    if (!next) return null;
    next.setFullYear(next.getFullYear() + (Number(parts?.yil) || 0));
    next.setMonth(next.getMonth() + (Number(parts?.ay) || 0));
    next.setDate(next.getDate() + (Number(parts?.gun) || 0) + (Number(parts?.extra) || 0));
    next.setHours(12, 0, 0, 0);
    return skipSunday(next, 1);
}

/** İki şablon satırı arasındaki işlem günü. Öncekinden say kapalıysa fark başlangıç ofsetindedir. */
function stepBetweenLines(fromLine, toLine) {
    if (toLine?.IS_ONCEKINDEN_SAY) {
        const parts = offsetParts(toLine);
        return { yil: parts.yil, ay: parts.ay, gun: parts.gun, extra: parts.extra };
    }
    const from = offsetParts(fromLine);
    const to = offsetParts(toLine);
    return {
        yil: to.yil - from.yil,
        ay: to.ay - from.ay,
        gun: (to.gun + to.extra) - (from.gun + from.extra),
        extra: 0,
    };
}

function neighborGapDays(sorted, row) {
    const idx = sorted.findIndex((item) => rowRef(item) === rowRef(row));
    if (idx <= 0) return 1;
    const gap = calcDeltaDays(row?.CI_RAN_TAR, sorted[idx - 1]?.CI_RAN_TAR);
    return gap > 0 ? gap : 1;
}

function templateSpan(line) {
    return (Number(line?.IS_YILSAY) || 0) + (Number(line?.IS_AYSAY) || 0) + (Number(line?.IS_GUNSAY) || 0);
}

/**
 * Eski tarih sırasındaki işlemleri, şablon adı + işlem türü ile şablon satırına bağlar.
 * Aynı tür birden fazlaysa tarih sırası şablon sırasını tüketir.
 */
export function matchIslemSablonLines(sortedRows, sablonLines) {
    const linesByName = new Map();
    (sablonLines || []).forEach((line) => {
        if (!line || isPassiveSablonLine(line)) return;
        const name = String(line.IS_AD ?? '').trim();
        if (!name) return;
        if (!linesByName.has(name)) linesByName.set(name, []);
        linesByName.get(name).push(line);
    });
    linesByName.forEach((lines) => {
        lines.sort((a, b) => (Number(a.IS_SIRA) || 0) - (Number(b.IS_SIRA) || 0));
    });

    const rowsByName = new Map();
    (sortedRows || []).forEach((row) => {
        const name = sablonAdOf(row);
        if (!name || !(rowRef(row) > 0)) return;
        if (!rowsByName.has(name)) rowsByName.set(name, []);
        rowsByName.get(name).push(row);
    });

    const matched = new Map();
    rowsByName.forEach((rows, name) => {
        const lines = linesByName.get(name) || [];
        const unassigned = new Set(rows.map((row) => rowRef(row)));
        lines.forEach((line) => {
            const found = rows.find((row) => (
                unassigned.has(rowRef(row)) && Number(row.CI_TUR) === Number(line.IS_TUR)
            ));
            if (!found) return;
            unassigned.delete(rowRef(found));
            matched.set(rowRef(found), line);
        });
    });
    return matched;
}

function positionAgainstSource(row, sourceRef, baseDate) {
    const rowDay = startOfDay(row?.CI_RAN_TAR);
    if (!rowDay || !baseDate) return 0;
    const cmp = rowDay.getTime() - baseDate.getTime();
    if (cmp < 0) return -1;
    if (cmp > 0) return 1;
    const ref = rowRef(row);
    if (ref < sourceRef) return -1;
    if (ref > sourceRef) return 1;
    return 0;
}

function nextDateForMovedRow(row, previous, arrivalDay, sorted, matchedLine) {
    if (matchedLine && templateSpan(matchedLine) > 0) {
        // Gün/ay/yıl çoğu şablonda başlangıçtan itibaren sayılır. Öncekinden say
        // yalnız şablonda açıksa bir önceki randevuya eklenir.
        return computeRandevuForSablonLine(matchedLine, arrivalDay, previous);
    }
    return skipSunday(addDays(previous, neighborGapDays(sorted, row)), 1);
}

function linesForSablon(rows, sablonLines) {
    const name = rows.map(sablonAdOf).find(Boolean);
    if (!name) return [];
    return (sablonLines || [])
        .filter((line) => line && !isPassiveSablonLine(line) && String(line.IS_AD ?? '').trim() === name)
        .sort((a, b) => (Number(a.IS_SIRA) || 0) - (Number(b.IS_SIRA) || 0));
}

/**
 * Şablon güncellemedeki formülle her satırın günü.
 * Aynı güne düşen satırlar tek adım sayılır; adımlar geldiği günden sonrakilerdir.
 */
function formulaSteps(arrivalDay, lines) {
    let previous = null;
    const lineDay = new Map();
    const unique = [];
    lines.forEach((line) => {
        const day = startOfDay(computeRandevuForSablonLine(line, arrivalDay, previous));
        previous = day;
        if (!day) return;
        lineDay.set(line, day);
        if (day.getTime() > arrivalDay.getTime() && !unique.some((item) => item.getTime() === day.getTime())) {
            unique.push(day);
        }
    });
    unique.sort((a, b) => a.getTime() - b.getTime());
    return { lineDay, unique };
}

function groupsByFormulaDay(queue, dayOf) {
    const groups = [];
    const indexByKey = new Map();
    queue.forEach((rec) => {
        const day = dayOf(rec);
        const key = day ? `day:${day.getTime()}` : `row:${rowRef(rec)}`;
        const existing = indexByKey.get(key);
        if (existing != null) {
            groups[existing].push(rec);
            return;
        }
        indexByKey.set(key, groups.length);
        groups.push([rec]);
    });
    return groups;
}

/**
 * Gelen işlemden önce gelmemiş randevu varsa, gelen gününden başlayarak
 * önce onları, sonra sonraki açık randevuları şablon aralığıyla yeniden dizer.
 * Önce gelmeyen yoksa active=false döner; çağıran mevcut gün-farkı kaydırmasını kullanır.
 */
export function planMissedBeforeReorder(islemList, sourceRow, arrivalVal, options = {}) {
    const empty = {
        active: false,
        updates: [],
        sourceRanTar: null,
        earlierMissedCount: 0,
        laterOpenCount: 0,
    };
    const sourceRef = rowRef(sourceRow);
    const arrivalDay = startOfDay(arrivalVal);
    if (!(sourceRef > 0) || !arrivalDay) return empty;

    const sourceCas = Number(options.casRef ?? sourceRow?.CI_CAS_REF ?? 0);
    const baseDate = startOfDay(sourceRow?.CI_RAN_TAR) || arrivalDay;
    const sameSale = [];
    (islemList || []).forEach((rec) => {
        if (!rec?.CI_RAN_TAR || !(rowRef(rec) > 0)) return;
        if (sourceCas > 0 && Number(rec?.CI_CAS_REF ?? 0) !== sourceCas) return;
        sameSale.push(rec);
    });
    if (!sameSale.some((rec) => rowRef(rec) === sourceRef) && sourceRow?.CI_RAN_TAR) {
        sameSale.push(sourceRow);
    }

    const sorted = [...sameSale].sort(compareByRanTar);
    const earlierMissed = [];
    const laterOpen = [];
    sorted.forEach((rec) => {
        if (rowRef(rec) === sourceRef) return;
        const position = positionAgainstSource(rec, sourceRef, baseDate);
        if (position === 0 || isCompletedRow(rec)) return;
        if (position < 0) earlierMissed.push(rec);
        else laterOpen.push(rec);
    });
    if (earlierMissed.length === 0) return empty;

    const queue = [...earlierMissed, ...laterOpen];
    const lines = linesForSablon([sourceRow, ...queue], options.sablonLines);
    const { lineDay, unique } = formulaSteps(arrivalDay, lines);
    const matched = matchIslemSablonLines(sorted, lines);
    const dayOf = (rec) => {
        const line = matched.get(rowRef(rec));
        return line ? lineDay.get(line) : null;
    };
    const sourceKey = dayOf(sourceRow)?.getTime() ?? null;
    const assigned = new Map();
    let previous = arrivalDay;
    let slot = 0;
    groupsByFormulaDay(queue, dayOf).forEach((group) => {
        const canon = dayOf(group[0]);
        const sharesArrivedDay = canon && sourceKey != null && canon.getTime() === sourceKey;
        let day;
        if (sharesArrivedDay) {
            day = arrivalDay;
        } else if (canon && unique[slot]) {
            day = unique[slot];
            slot += 1;
        } else {
            day = startOfDay(nextDateForMovedRow(
                group[0],
                previous,
                arrivalDay,
                sorted,
                matched.get(rowRef(group[0]))
            ));
        }
        previous = day || previous;
        group.forEach((rec) => assigned.set(rowRef(rec), day));
    });
    const updates = queue.map((rec) => ({
        CI_REF: rowRef(rec),
        CI_RAN_TAR: assigned.get(rowRef(rec)),
    }));

    return {
        active: true,
        updates: updates.filter((row) => row.CI_REF > 0),
        sourceRanTar: arrivalDay,
        earlierMissedCount: earlierMissed.length,
        laterOpenCount: laterOpen.length,
    };
}

/**
 * Yeni manuel satırın randevu günü: son kaydın tarihine, şablondaki bir sonraki
 * işlem aralığı eklenir. Şablon yoksa son iki farklı günün farkı, o da yoksa 15 gün.
 */
export function nextRandevuFromLastRow(rows, sablonLines) {
    const saved = (rows || []).filter((row) => rowRef(row) > 0 && row?.CI_RAN_TAR).sort(compareByRanTar);
    if (!saved.length) return applyOffset(new Date(), { gun: 0 });

    const lastDay = startOfDay(saved[saved.length - 1].CI_RAN_TAR);
    const lines = linesForSablon(saved, sablonLines);
    let step = null;
    if (lines.length && lastDay) {
        const anchor = startOfDay(saved[0].CI_RAN_TAR) || lastDay;
        const { lineDay } = formulaSteps(anchor, lines);
        const matched = matchIslemSablonLines(saved, lines);
        const onLastDay = saved.filter((row) => startOfDay(row.CI_RAN_TAR)?.getTime() === lastDay.getTime());
        let lastLine = null;
        let lastFormula = null;
        onLastDay.forEach((row) => {
            const line = matched.get(rowRef(row));
            const day = line ? lineDay.get(line) : null;
            if (!line || !day) return;
            const laterDay = !lastFormula || day.getTime() > lastFormula.getTime();
            const sameDayLater = lastFormula && day.getTime() === lastFormula.getTime()
                && (Number(line.IS_SIRA) || 0) > (Number(lastLine?.IS_SIRA) || 0);
            if (laterDay || sameDayLater) {
                lastFormula = day;
                lastLine = line;
            }
        });
        if (lastLine && lastFormula) {
            const next = lines
                .map((line) => ({ line, day: lineDay.get(line) }))
                .filter((item) => item.day && item.day.getTime() > lastFormula.getTime())
                .sort((a, b) => a.day.getTime() - b.day.getTime()
                    || (Number(a.line.IS_SIRA) || 0) - (Number(b.line.IS_SIRA) || 0))[0];
            if (next) {
                step = stepBetweenLines(lastLine, next.line);
            } else {
                const distinct = [];
                lines.forEach((line) => {
                    const day = lineDay.get(line);
                    if (!day || distinct.some((item) => item.day.getTime() === day.getTime())) return;
                    distinct.push({ line, day });
                });
                distinct.sort((a, b) => a.day.getTime() - b.day.getTime());
                if (distinct.length >= 2) {
                    step = stepBetweenLines(distinct[distinct.length - 2].line, distinct[distinct.length - 1].line);
                }
            }
        }
    }
    if (!step || ((Number(step.yil) || 0) === 0 && (Number(step.ay) || 0) === 0 && (Number(step.gun) || 0) <= 0 && (Number(step.extra) || 0) <= 0)) {
        const distinctDays = [];
        saved.forEach((row) => {
            const day = startOfDay(row.CI_RAN_TAR);
            if (day && !distinctDays.some((item) => item.getTime() === day.getTime())) distinctDays.push(day);
        });
        const gap = distinctDays.length >= 2
            ? calcDeltaDays(distinctDays[distinctDays.length - 1], distinctDays[distinctDays.length - 2])
            : 15;
        step = { yil: 0, ay: 0, gun: gap > 0 ? gap : 15, extra: 0 };
    }
    return applyOffset(lastDay, step);
}

function subtractOffset(date, line) {
    const parts = offsetParts(line);
    const next = startOfDay(date);
    if (!next) return null;
    next.setFullYear(next.getFullYear() - parts.yil);
    next.setMonth(next.getMonth() - parts.ay);
    next.setDate(next.getDate() - parts.gun - parts.extra);
    return startOfDay(next);
}

function linesNamed(sablonName, sablonLines) {
    const name = String(sablonName ?? '').trim();
    if (!name) return [];
    return linesForSablon([{ CI_DETAY: { sablonAd: name } }], sablonLines);
}

function seriesStartCandidates(rows, lines) {
    const matched = matchIslemSablonLines(rows, lines);
    const candidates = [];
    const push = (day) => {
        const start = startOfDay(day);
        if (!start) return;
        if (!candidates.some((item) => item.getTime() === start.getTime())) candidates.push(start);
    };
    rows.forEach((row) => {
        const line = matched.get(rowRef(row));
        if (!line || line.IS_ONCEKINDEN_SAY) return;
        const day = startOfDay(row?.CI_RAN_TAR);
        if (!day) return;
        push(subtractOffset(day, line));
        if (day.getDay() === 1) push(subtractOffset(addDays(day, -1), line));
    });
    return { matched, candidates };
}

function scoreSeriesStart(start, rows, lines, matched) {
    let previous = null;
    const lineDay = new Map();
    lines.forEach((line) => {
        const day = startOfDay(computeRandevuForSablonLine(line, start, previous));
        previous = day;
        if (day) lineDay.set(line, day);
    });
    let score = 0;
    rows.forEach((row) => {
        const line = matched.get(rowRef(row));
        const expected = line ? lineDay.get(line) : null;
        const actual = startOfDay(row?.CI_RAN_TAR);
        if (expected && actual && expected.getTime() === actual.getTime()) score += 1;
    });
    return score;
}

/**
 * Yeni satırda seçilen paket içeriğinin randevu günü.
 * Şablon satırı başlangıçtan ya da bir öncekinden sayılır; seri başlangıcı mevcut kayıtlardan bulunur.
 */
export function randevuForSablonTur(rows, sablonLines, sablonName, ciTur) {
    const tur = Number(ciTur);
    const lines = linesNamed(sablonName, sablonLines);
    if (!(tur > 0) || !lines.length) return null;
    const saved = (rows || []).filter((row) => rowRef(row) > 0 && row?.CI_RAN_TAR).sort(compareByRanTar);
    const { matched, candidates } = seriesStartCandidates(saved, lines);
    const used = new Set(matched.values());
    const sameTur = lines.filter((line) => Number(line.IS_TUR) === tur);
    const target = sameTur.find((line) => !used.has(line)) || sameTur[0];
    if (!target) return null;

    let best = null;
    let bestScore = -1;
    candidates.forEach((start) => {
        const score = scoreSeriesStart(start, saved, lines, matched);
        if (score > bestScore) {
            bestScore = score;
            best = start;
        }
    });
    const baslangic = bestScore > 0 ? best : (startOfDay(saved[0]?.CI_RAN_TAR) || startOfDay(new Date()));
    let previous = null;
    let result = null;
    lines.forEach((line) => {
        const day = computeRandevuForSablonLine(line, baslangic, previous);
        previous = day;
        if (line === target) result = day;
    });
    if (!result) return null;
    result.setHours(12, 0, 0, 0);
    return result;
}

export function formatMissedBeforeReorderMessage(earlierMissedCount, laterOpenCount) {
    const later = Number(laterOpenCount) > 0
        ? ` Ondan sonraki ${laterOpenCount} açık randevu bunların altına alındı.`
        : '';
    return `${earlierMissedCount} gelmemiş önceki randevu, gelen işlemin altına alındı.${later}`;
}

export function confirmMissedBeforeReorder(earlierMissedCount, laterOpenCount) {
    const later = Number(laterOpenCount) > 0
        ? `\nOndan sonraki ${laterOpenCount} açık randevu da bunların altına yazılacak.`
        : '';
    return window.confirm(
        `Bu işlemden önce gelmemiş ${earlierMissedCount} randevu var.\n\n` +
        'Gelen işlem onların üstüne alınacak; o randevuların tarihleri şablon aralığına göre altına yazılacak.' +
        later +
        '\n\nDevam edilsin mi?'
    );
}

export function planIslemDateShift(islemList, sourceRow, dataField, newValue, previousValue, options = {}) {
    if (!sourceRow) return { proceed: true, updates: [], deltaDays: 0 };

    let deltaDays = 0;
    let baseRanTar = sourceRow.CI_RAN_TAR;

    if (dataField === 'CI_ISL_TAR') {
        if (!newValue || !sourceRow.CI_RAN_TAR) return { proceed: true, updates: [], deltaDays: 0 };
        deltaDays = calcDeltaDays(newValue, sourceRow.CI_RAN_TAR);
        baseRanTar = sourceRow.CI_RAN_TAR;
    } else if (dataField === 'CI_RAN_TAR') {
        if (!newValue || !previousValue) return { proceed: true, updates: [], deltaDays: 0 };
        deltaDays = calcDeltaDays(newValue, previousValue);
        baseRanTar = previousValue;
    } else {
        return { proceed: true, updates: [], deltaDays: 0 };
    }

    if (!deltaDays) return { proceed: true, updates: [], deltaDays: 0 };

    const { updates, shiftedCount } = computeRandevuOteleme(islemList, sourceRow, deltaDays, {
        ...options,
        baseRanTar,
    });

    return { proceed: true, updates, shiftedCount, deltaDays, needsConfirm: true };
}
